/* eslint-disable */
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppData } from '../../context/AppDataContext';
import { tenantsAPI } from '../../services/api';
import { isIrishProperty, effectiveJurisdiction } from '../../utils/propertyCountry';
import { symbolFor } from '../../utils/currencyTotals';

// ── Helpers ──────────────────────────────────────────────────────────────────

const today = new Date();

const fmtDate = (d) => {
  if (!d) return '—';
  const s = typeof d === 'string' ? d.substring(0, 10) : d;
  const [y, m, day] = s.split('-');
  return `${day}/${m}/${y}`;
};

const daysBetween = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr.substring(0, 10) + 'T12:00:00');
  return Math.round((d - today) / 86400000);
};

const leaseStatus = (t) => {
  if (!t.lease_end) return { label: 'Periodic', color: 'var(--text-secondary)' };
  const days = daysBetween(t.lease_end);
  if (days < 0)   return { label: 'Expired',        color: 'var(--danger)' };
  if (days <= 30) return { label: `Expires in ${days}d`, color: 'var(--danger)' };
  if (days <= 60) return { label: `Expires in ${days}d`, color: 'var(--warning)' };
  return { label: fmtDate(t.lease_end), color: 'var(--success)' };
};

// Tenancy type options by jurisdiction
const TENANCY_TYPES = {
  england: [
    { value: 'assured_periodic',  label: 'Assured Periodic Tenancy (post-RRA)' },
    { value: 'assured_fixed',     label: 'Assured Tenancy Fixed Term (pre-RRA)' },
    { value: 'licence',           label: 'Licence to Occupy' },
    { value: 'company_let',       label: 'Company Let' },
    { value: 'student_hmo',       label: 'Student HMO Licence' },
  ],
  scotland: [
    { value: 'prt',               label: 'Private Residential Tenancy (PRT)' },
    { value: 'licence',           label: 'Licence to Occupy' },
  ],
  wales: [
    { value: 'occupation_contract', label: 'Occupation Contract' },
    { value: 'licence',             label: 'Licence to Occupy' },
  ],
  northern_ireland: [
    { value: 'private_tenancy',   label: 'Private Tenancy' },
    { value: 'licence',           label: 'Licence to Occupy' },
  ],
  ireland: [
    { value: 'part4',             label: 'Part 4 Tenancy' },
    { value: 'further_part4',     label: 'Further Part 4 Tenancy' },
    { value: 'fixed_term',        label: 'Fixed Term Tenancy' },
    { value: 'licence',           label: 'Licence to Occupy' },
    { value: 'student_specific',  label: 'Student Specific Accommodation' },
  ],
};

// Deposit schemes by jurisdiction
const DEPOSIT_SCHEMES = {
  england:          ['DPS (Deposit Protection Service)', 'MyDeposits', 'TDS (Tenancy Deposit Scheme)', 'Other'],
  scotland:         ['SafeDeposits Scotland', 'MyDeposits Scotland', 'Letting Protection Service Scotland', 'Other'],
  wales:            ['DPS (Deposit Protection Service)', 'MyDeposits', 'TDS (Tenancy Deposit Scheme)', 'Other'],
  northern_ireland: ['TDS Northern Ireland', 'Other'],
  ireland:          ['RTB Deposit Protection (incoming)', 'Held by landlord', 'Held by agency', 'Other'],
};

// Checklist definitions by jurisdiction
const CHECKLIST_ITEMS = {
  england: [
    { key: 'tenancy_agreement_signed',    label: 'Tenancy agreement signed' },
    { key: 'deposit_taken',               label: 'Deposit taken' },
    { key: 'deposit_protected',           label: 'Deposit protected in scheme' },
    { key: 'right_to_rent_checked',       label: 'Right to Rent check completed' },
    { key: 'inventory_completed',         label: 'Inventory / check-in report completed' },
    { key: 'keys_handed_over',            label: 'Keys handed over' },
    { key: 'standing_order_confirmed',    label: 'Standing order / direct debit set up' },
    { key: 'gas_safety_cert_provided',    label: 'Gas Safety Certificate provided' },
    { key: 'epc_provided',                label: 'EPC (Energy Performance Certificate) provided' },
    { key: 'how_to_rent_guide_provided',  label: '"How to Rent" guide provided' },
  ],
  scotland: [
    { key: 'tenancy_agreement_signed',    label: 'Tenancy agreement signed' },
    { key: 'deposit_taken',               label: 'Deposit taken' },
    { key: 'deposit_protected',           label: 'Deposit protected in scheme' },
    { key: 'inventory_completed',         label: 'Inventory completed' },
    { key: 'keys_handed_over',            label: 'Keys handed over' },
    { key: 'gas_safety_cert_provided',    label: 'Gas Safety Certificate provided' },
    { key: 'epc_provided',                label: 'EPC provided' },
    { key: 'tenants_rights_info',         label: 'Tenant rights information provided' },
  ],
  wales: [
    { key: 'tenancy_agreement_signed',    label: 'Occupation contract signed' },
    { key: 'deposit_taken',               label: 'Deposit taken' },
    { key: 'deposit_protected',           label: 'Deposit protected in scheme' },
    { key: 'right_to_rent_checked',       label: 'Right to Rent check completed' },
    { key: 'inventory_completed',         label: 'Inventory completed' },
    { key: 'keys_handed_over',            label: 'Keys handed over' },
    { key: 'gas_safety_cert_provided',    label: 'Gas Safety Certificate provided' },
    { key: 'epc_provided',                label: 'EPC provided' },
  ],
  northern_ireland: [
    { key: 'tenancy_agreement_signed',    label: 'Tenancy agreement signed' },
    { key: 'deposit_taken',               label: 'Deposit taken' },
    { key: 'deposit_protected',           label: 'Deposit protected in scheme' },
    { key: 'inventory_completed',         label: 'Inventory completed' },
    { key: 'keys_handed_over',            label: 'Keys handed over' },
    { key: 'gas_safety_cert_provided',    label: 'Gas Safety Certificate provided' },
    { key: 'epc_provided',                label: 'EPC provided' },
  ],
  ireland: [
    { key: 'tenancy_agreement_signed',    label: 'Tenancy agreement signed' },
    { key: 'deposit_taken',               label: 'Deposit taken' },
    { key: 'deposit_protected',           label: 'Deposit held (per RTB rules)' },
    { key: 'rtb_registered',              label: 'Tenancy registered with RTB' },
    { key: 'inventory_completed',         label: 'Inventory completed' },
    { key: 'keys_handed_over',            label: 'Keys handed over' },
    { key: 'gas_safety_cert_provided',    label: 'Gas Safety Certificate provided' },
    { key: 'ber_cert_provided',           label: 'BER Certificate provided' },
    { key: 'rent_book_provided',          label: 'Rent book provided' },
    { key: 'rpz_compliance_confirmed',    label: 'RPZ compliance confirmed (if applicable)' },
  ],
};

const defaultChecklist = (jurisdiction) => {
  const items = CHECKLIST_ITEMS[jurisdiction] || CHECKLIST_ITEMS.england;
  return items.reduce((acc, i) => ({ ...acc, [i.key]: false }), {});
};

// BUG-005e: one rule for the symbol (shared helper)
const currencySymbol = (currency) => symbolFor(currency);

// IC-006: Derive Irish compliance issues for a tenant
const getIrishComplianceIssues = (t) => {
  if (!isIrishProperty(t)) return [];
  const issues = [];
  const now = new Date();

  // RTB not registered and lease started >25 days ago
  if (!t.rtb_registered && t.lease_start) {
    const leaseStart = new Date(t.lease_start);
    const daysSince = Math.floor((now - leaseStart) / 86400000);
    if (daysSince >= 25) {
      const deadline = new Date(leaseStart);
      deadline.setDate(deadline.getDate() + 30);
      issues.push({ code: 'IC-001', label: 'RTB Registration Due', severity: daysSince >= 30 ? 'danger' : 'warning', deadline: deadline.toLocaleDateString('en-IE') });
    }
  }

  // RPZ rent review due
  if (t.rpz_property) {
    const reviewBase = t.last_rent_review_date || t.lease_start;
    if (reviewBase) {
      const reviewDue = new Date(reviewBase);
      reviewDue.setFullYear(reviewDue.getFullYear() + 1);
      reviewDue.setDate(reviewDue.getDate() - 30); // warn 30 days before
      if (now >= reviewDue) issues.push({ code: 'IC-002', label: 'Rent Review Due', severity: 'warning' });
    }
  }

  // Approaching 6 year Further Part 4
  if (t.lease_start) {
    const sixYears = new Date(t.lease_start);
    sixYears.setFullYear(sixYears.getFullYear() + 6);
    const daysTo6yr = Math.floor((sixYears - now) / 86400000);
    if (daysTo6yr >= 0 && daysTo6yr <= 60) issues.push({ code: 'IC-005', label: `Further Part 4 in ${daysTo6yr}d`, severity: 'warning' });
  }

  return issues;
};

const fmtRent = (amount, currency) => {
  if (!amount) return '—';
  const sym = currencySymbol(currency);
  return `${sym}${parseFloat(amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
};

const defaultForm = (jurisdiction = 'england') => ({
  first_name: '', last_name: '', email: '', phone: '', date_of_birth: '',
  emergency_name: '', emergency_phone: '', emergency_relationship: '',
  guarantor_name: '', guarantor_email: '', guarantor_phone: '',
  lease_start: '', lease_end: '', rent_amount: '', payment_due_day: 1,
  tenancy_type: jurisdiction === 'ireland' ? 'part4' : 'assured_periodic',
  deposit_amount: '', deposit_scheme: '', deposit_reference: '',
  deposit_paid_date: '', deposit_protected_date: '',
  right_to_rent_checked: false, right_to_rent_date: '', right_to_rent_doc: '', right_to_rent_expiry: '',
  rtb_registered: false, rtb_registration_number: '', rtb_registration_date: '',
  rpz_property: false, last_rent_review_date: '',
  checklist: defaultChecklist(jurisdiction),
  status: 'active', notes: '',
  email_reminders: true, email_receipts: true, comms_override: false,
  property_id: '', room_id: '',
});

// ── Sub-components ────────────────────────────────────────────────────────────

function ChecklistPanel({ checklist, jurisdiction, onChange, readOnly }) {
  const items = CHECKLIST_ITEMS[jurisdiction] || CHECKLIST_ITEMS.england;
  const done  = items.filter(i => checklist[i.key]).length;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <span style={{ fontWeight: 600 }}>Move-in Checklist</span>
        <span style={{ fontSize: '0.8rem', color: done === items.length ? 'var(--success)' : 'var(--text-secondary)' }}>
          {done}/{items.length} complete
        </span>
      </div>
      <div style={{ display: 'grid', gap: '0.5rem' }}>
        {items.map(item => (
          <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: readOnly ? 'default' : 'pointer', fontSize: '0.875rem' }}>
            <input
              type="checkbox"
              checked={!!checklist[item.key]}
              onChange={readOnly ? undefined : () => onChange({ ...checklist, [item.key]: !checklist[item.key] })}
              disabled={readOnly}
              style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }}
            />
            <span style={{ color: checklist[item.key] ? 'var(--text-secondary)' : 'var(--text-primary)', textDecoration: checklist[item.key] ? 'line-through' : 'none' }}>
              {item.label}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function Tenants() {
  const { tenants, setTenants, properties } = useAppData();
  const navigate = useNavigate();

  // Helper to get room name from property rooms
  const getRoomName = (propertyId, roomId) => {
    const prop = properties.find(p => String(p.id) === String(propertyId));
    const room = prop?.rooms?.find(r => String(r.id) === String(roomId));
    return room?.roomName || roomId;
  };

  const [search, setSearch]         = useState('');
  const [filterStatus, setFilter]   = useState('active');
  const [filterProp, setFilterProp] = useState('');
  const [groupBy, setGroupBy]       = useState('property'); // 'property' | 'alpha'
  const [modalOpen, setModalOpen]   = useState(false);
  const [editTenant, setEditTenant] = useState(null);
  const [form, setForm]             = useState(defaultForm());

  // BACKDATE-001: Warn (don't block) if the entered lease dates overlap with
  // another tenancy already recorded for the same room — catches likely
  // date-entry mistakes while still allowing genuine overlaps (handovers,
  // joint tenancies).
  const overlapWarning = useMemo(() => {
    if (!form.property_id || !form.lease_start) return null;
    // BUG FIX: this only ever matched by room_id — fine for an HMO, but a
    // BTL/single-let property now legitimately has no room_id at all, so
    // this check silently never fired for them, missing exactly the kind
    // of overlapping-dates mistake it exists to catch. isHMO isn't in scope
    // yet at this point in the file, so looked up independently here
    // rather than reordering other declarations.
    const propIsHMO = properties.find(p => String(p.id) === String(form.property_id))?.property_type === 'hmo';
    if (propIsHMO && !form.room_id) return null;
    const newStart = form.lease_start;
    const newEnd = form.lease_end || '9999-12-31'; // open-ended / periodic tenancy
    const overlapping = tenants.filter(t => {
      if (editTenant && t.id === editTenant.id) return false;
      if (propIsHMO) {
        if (String(t.room_id) !== String(form.room_id)) return false;
      } else {
        if (String(t.property_id) !== String(form.property_id)) return false;
      }
      if (!t.lease_start) return false;
      const existingStart = t.lease_start.substring(0,10);
      const existingEnd = (t.lease_end || '9999-12-31').substring(0,10);
      return newStart <= existingEnd && existingStart <= newEnd;
    });
    if (!overlapping.length) return null;
    const names = overlapping.map(t => `${t.first_name} ${t.last_name||''}`.trim()).join(', ');
    return `These dates overlap with ${names}'s tenancy in this ${propIsHMO ? 'room' : 'property'}. This is allowed (e.g. a handover or joint tenancy) — just worth double-checking the dates are correct.`;
  }, [form.property_id, form.room_id, form.lease_start, form.lease_end, tenants, editTenant, properties]);
  const [loading, setLoading]       = useState(false);
  // BUG FIX: this file has its own separate "Move Out" quick action (list
  // view), entirely independent from the one in TenantDetail.js. The
  // backdating fix built earlier today only touched TenantDetail.js's
  // version — this one still used a blind window.confirm() that always
  // recorded TODAY as the move-out date, with no way to reflect an actual
  // earlier departure. Same fix, same modal pattern, applied here too.
  const [showMoveOutModal, setShowMoveOutModal] = useState(false);
  const [moveOutTarget, setMoveOutTarget] = useState(null);
  const [moveOutDateInput, setMoveOutDateInput] = useState('');
  const [movingOut, setMovingOut] = useState(false);
  const [moveOutError, setMoveOutError] = useState('');
  const todayISO = () => new Date().toISOString().substring(0,10);
  // Agency-managed properties: Right to Rent/RTB and the move-in checklist
  // are the agency's responsibility, not something the landlord needs to
  // fill in on this form. Kept accessible (not hidden — the property could
  // change management type, or the landlord may want to record it anyway)
  // but collapsed by default so it doesn't compete for attention with the
  // fields that actually matter for an agency-managed tenancy. Only
  // affects agency properties — self-managed ones are unchanged.
  const [showComplianceDetails, setShowComplianceDetails] = useState(false);
  const [error, setError]           = useState('');

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  // Derive jurisdiction from selected property
  const selectedProperty = useMemo(() =>
    properties.find(p => String(p.id) === String(form.property_id)),
    [properties, form.property_id]
  );
  const jurisdiction = effectiveJurisdiction(selectedProperty);
  const isIreland    = jurisdiction === 'ireland';
  const isAgency     = selectedProperty?.management_type === 'agency';
  // BUG FIX: BTL/single-let properties were being forced through the same
  // "select a room" step as HMOs, even though they have no rooms at all —
  // asking for something that doesn't exist for that property type. Only
  // HMOs have rooms; every other property type (btl, single-let,
  // holiday-let, commercial) is one tenancy for the whole property, same
  // two-way split already used consistently in server.js/Reports.js.
  const isHMO        = selectedProperty?.property_type === 'hmo';

  // Rooms for selected property
  const availableRooms = useMemo(() =>
    (selectedProperty?.rooms || []),
    [selectedProperty]
  );

  // Filtered tenant list
  const filtered = useMemo(() => {
    let list = tenants;
    if (filterStatus !== 'all') list = list.filter(t => t.status === filterStatus);
    if (filterProp)             list = list.filter(t => String(t.property_id) === filterProp);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        `${t.first_name} ${t.last_name}`.toLowerCase().includes(q) ||
        (t.email || '').toLowerCase().includes(q) ||
        (t.property_address || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [tenants, filterStatus, filterProp, search]);

  // Grouped display data
  const groupedTenants = useMemo(() => {
    if (groupBy === 'alpha') {
      const sorted = [...filtered].sort((a, b) => {
        const last = (a.last_name || '').localeCompare(b.last_name || '');
        if (last !== 0) return last;
        return (a.first_name || '').localeCompare(b.first_name || '');
      });
      return [{ key: 'alpha', label: null, propertyType: null, tenancies: [{ ref: 'alpha', tenants: sorted }] }];
    }

    // Group by property first
    const propGroups = {};
    [...filtered].forEach(t => {
      const key = t.property_address || 'Unknown Property';
      if (!propGroups[key]) propGroups[key] = {
        key, label: key,
        propertyType: t.property_type || 'hmo',
        propertyId: t.property_id,
        jurisdiction: effectiveJurisdiction(t),
        currency: t.currency,
        tenancies: {},
      };
      // Within each property group by tenancy_ref
      const ref = t.tenancy_ref || `single_${t.id}`;
      if (!propGroups[key].tenancies[ref]) propGroups[key].tenancies[ref] = { ref, tenants: [] };
      propGroups[key].tenancies[ref].tenants.push(t);
    });

    // Convert tenancies obj to sorted array, lead tenant first
    return Object.values(propGroups)
      .sort((a, b) => a.key.localeCompare(b.key))
      .map(g => ({
        ...g,
        tenancies: Object.values(g.tenancies).map(ten => ({
          ...ten,
          tenants: [...ten.tenants].sort((a, b) => (b.is_lead_tenant ? 1 : 0) - (a.is_lead_tenant ? 1 : 0)),
        })),
      }));
  }, [filtered, groupBy]);

  // Stats
  const stats = useMemo(() => {
    const active   = tenants.filter(t => t.status === 'active').length;
    const expiring = tenants.filter(t => {
      if (t.status !== 'active' || !t.lease_end) return false;
      const d = daysBetween(t.lease_end);
      return d !== null && d >= 0 && d <= 60;
    }).length;
    const arrears = tenants.filter(t => t.status === 'active').length; // placeholder — will wire to payments
    return { active, expiring, total: tenants.length };
  }, [tenants]);

  // Open add modal
  const openAdd = () => {
    setEditTenant(null);
    setForm(defaultForm());
    setError('');
    setModalOpen(true);
  };

  // Open edit modal
  const openEdit = (t) => {
    setEditTenant(t);
    setForm({
      first_name: t.first_name || '', last_name: t.last_name || '',
      email: t.email || '', phone: t.phone || '', date_of_birth: t.date_of_birth?.substring(0,10) || '',
      emergency_name: t.emergency_name || '', emergency_phone: t.emergency_phone || '',
      emergency_relationship: t.emergency_relationship || '',
      guarantor_name: t.guarantor_name || '', guarantor_email: t.guarantor_email || '',
      guarantor_phone: t.guarantor_phone || '',
      lease_start: t.lease_start?.substring(0,10) || '',
      lease_end: t.lease_end?.substring(0,10) || '',
      rent_amount: t.rent_amount || '', payment_due_day: t.payment_due_day || 1,
      tenancy_type: t.tenancy_type || 'assured_periodic',
      deposit_amount: t.deposit_amount || '', deposit_scheme: t.deposit_scheme || '',
      deposit_reference: t.deposit_reference || '',
      deposit_paid_date: t.deposit_paid_date?.substring(0,10) || '',
      deposit_protected_date: t.deposit_protected_date?.substring(0,10) || '',
      right_to_rent_checked: t.right_to_rent_checked || false,
      right_to_rent_date: t.right_to_rent_date?.substring(0,10) || '',
      right_to_rent_doc: t.right_to_rent_doc || '',
      right_to_rent_expiry: t.right_to_rent_expiry?.substring(0,10) || '',
      rtb_registered: t.rtb_registered || false,
      rtb_registration_number: t.rtb_registration_number || '',
      rtb_registration_date: t.rtb_registration_date?.substring(0,10) || '',
      rpz_property: t.rpz_property || false,
      last_rent_review_date: t.last_rent_review_date?.substring(0,10) || '',
      checklist: t.checklist || defaultChecklist(effectiveJurisdiction(t)),
      status: t.status || 'active',
      move_out_date: t.move_out_date?.substring(0,10) || '',
      move_out_notes: t.move_out_notes || '',
      notes: t.notes || '',
      email_reminders: t.email_reminders !== false,
      email_receipts: t.email_receipts !== false,
      comms_override: t.comms_override || false,
      property_id: t.property_id || '', room_id: t.room_id || '',
    });
    setError('');
    setModalOpen(true);
  };


  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.first_name.trim()) { setError('First name is required'); return; }
    if (!form.property_id)       { setError('Please select a property'); return; }
    if (isHMO && !form.room_id)  { setError('Please select a room'); return; }
    if (form.status === 'moved_out' && !form.lease_end) { setError('A past tenant needs a Lease End date'); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        rent_amount:    form.rent_amount    ? parseFloat(form.rent_amount)    : null,
        deposit_amount: form.deposit_amount ? parseFloat(form.deposit_amount) : null,
        payment_due_day: parseInt(form.payment_due_day) || 1,
        // If agency managed and no comms override, default comms off
        email_reminders: isAgency && !form.comms_override ? false : form.email_reminders,
        email_receipts:  isAgency && !form.comms_override ? false : form.email_receipts,
      };
      let result;
      if (editTenant) {
        result = await tenantsAPI.update(editTenant.id, payload);
        setTenants(prev => prev.map(t => t.id === editTenant.id ? result : t));
      } else {
        result = await tenantsAPI.create(payload);
        setTenants(prev => [result, ...prev]);
      }
      setModalOpen(false);
    } catch (err) {
      setError(err?.response?.data?.error || err?.error || 'Failed to save tenant');
    } finally { setLoading(false); }
  };

  const handleMoveOut = async () => {
    if (!moveOutTarget) return;
    setMoveOutError('');
    if (moveOutDateInput > todayISO()) { setMoveOutError('Move-out date cannot be in the future.'); return; }
    setMovingOut(true);
    try {
      await tenantsAPI.moveOut(moveOutTarget.id, moveOutDateInput || undefined);
      setTenants(prev => prev.map(x => x.id === moveOutTarget.id ? { ...x, status: 'moved_out', move_out_date: moveOutDateInput || todayISO() } : x));
      setShowMoveOutModal(false);
      setMoveOutTarget(null);
    } catch (e) { setMoveOutError(e.error || 'Failed to update tenant'); }
    finally { setMovingOut(false); }
  };

  // When property changes, reset room and update checklist
  const handlePropertyChange = (propId) => {
    const prop = properties.find(p => String(p.id) === String(propId));
    const jur  = effectiveJurisdiction(prop);
    setForm(f => ({
      ...f,
      property_id: propId,
      room_id: '',
      checklist: defaultChecklist(jur),
      tenancy_type: jur === 'ireland' ? 'part4' : 'assured_periodic',
      email_reminders: prop?.management_type === 'agency' ? false : true,
      email_receipts:  prop?.management_type === 'agency' ? false : true,
    }));
  };

  const tenancyTypes   = TENANCY_TYPES[jurisdiction]   || TENANCY_TYPES.england;
  const depositSchemes = DEPOSIT_SCHEMES[jurisdiction] || DEPOSIT_SCHEMES.england;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Tenants</h1>
          <p className="page-subtitle">{stats.active} active · {stats.expiring > 0 ? `${stats.expiring} leases expiring soon` : 'All leases current'}</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Tenant</button>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Active Tenants',       value: stats.active,   color: 'var(--success)' },
          { label: 'Leases Expiring (60d)', value: stats.expiring, color: stats.expiring > 0 ? 'var(--warning)' : 'var(--success)' },
          { label: 'Total (inc. past)',     value: stats.total,    color: 'var(--text-secondary)' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="form-control" style={{ maxWidth: '260px' }}
            placeholder="Search tenants..."
            value={search} onChange={e => setSearch(e.target.value)}
          />
          <select className="form-control" style={{ maxWidth: '160px' }} value={filterStatus} onChange={e => setFilter(e.target.value)}>
            <option value="active">Active</option>
            <option value="moved_out">Moved Out</option>
            <option value="all">All</option>
          </select>
          <select className="form-control" style={{ maxWidth: '240px' }} value={filterProp} onChange={e => setFilterProp(e.target.value)}>
            <option value="">All Properties</option>
            {properties.filter(p => !p.archived).map(p => (
              <option key={p.id} value={String(p.id)}>{p.address}</option>
            ))}
          </select>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
            {filtered.length} tenant{filtered.length !== 1 ? 's' : ''}
          </span>
          <div style={{ display: 'flex', gap: '0.25rem', marginLeft: '0.5rem' }}>
            <button
              className={`btn ${groupBy === 'property' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
              onClick={() => setGroupBy('property')}
              title="Group by property"
            >🏠 Property</button>
            <button
              className={`btn ${groupBy === 'alpha' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
              onClick={() => setGroupBy('alpha')}
              title="Sort alphabetically"
            >A–Z</button>
          </div>
        </div>
      </div>

      {/* Tenants list */}
      {filtered.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <span>👤</span>
            <h3>No tenants found</h3>
            <p>{filterStatus === 'active' ? 'Add your first tenant to get started' : 'No tenants match your filters'}</p>
          </div>
        </div>
      ) : (
        <div>
          {groupedTenants.map(group => {
            const isHMO = group.propertyType === 'hmo';
            const propTypeLabel = { hmo: 'HMO', btl: 'BTL', single_let: 'Single Let', 'single-let': 'Single Let', holiday_let: 'Holiday Let', 'holiday-let': 'Holiday Let', commercial: 'Commercial' }[group.propertyType] || group.propertyType || '';

            return (
              <div key={group.key} className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: '1rem' }}>
                {/* Property header */}
                {group.label && (
                  <div style={{ padding: '0.6rem 1rem', background: 'var(--bg-light)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <i className="ti ti-building" style={{ color: 'var(--primary)' }} />
                    <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{group.label}</span>
                    {propTypeLabel && <span style={{ fontSize: '0.7rem', background: 'var(--primary)', color: '#fff', borderRadius: '0.25rem', padding: '0.1rem 0.4rem', fontWeight: 600 }}>{propTypeLabel}</span>}
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
                      {group.tenancies.reduce((s, ten) => s + ten.tenants.length, 0)} tenant{group.tenancies.reduce((s, ten) => s + ten.tenants.length, 0) !== 1 ? 's' : ''}
                    </span>
                  </div>
                )}

                {/* ── HMO: room-by-room table ── */}
                {(isHMO || groupBy === 'alpha') && (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                      <thead>
                        <tr style={{ background: 'var(--bg-light)', borderBottom: '1px solid var(--border)' }}>
                          {['Tenant', groupBy === 'alpha' ? 'Property / Room' : 'Room', 'Rent', 'Lease Status', 'Checklist', 'Comms', ''].map(h => (
                            <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {group.tenancies.flatMap(ten => ten.tenants).map((t, i) => {
                          const ls        = leaseStatus(t);
                          const checklist = t.checklist || {};
                          const items     = CHECKLIST_ITEMS[effectiveJurisdiction(t)] || CHECKLIST_ITEMS.england;
                          const done      = items.filter(it => checklist[it.key]).length;
                          const isAgencyRow = t.management_type === 'agency';
                          const currency  = t.currency || 'GBP';
                          return (
                            <tr key={t.id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--bg-light)', cursor: 'pointer' }}
                              onClick={() => navigate(`/tenants/${t.id}`)}>
                              <td style={{ padding: '0.75rem 1rem' }}>
                                <div style={{ fontWeight: 600 }}>{t.first_name} {t.last_name}</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{t.email || '—'}</div>
                                {t.status === 'moved_out' && <span style={{ fontSize: '0.7rem', background: 'var(--bg-light)', border: '1px solid var(--border)', borderRadius: '0.25rem', padding: '0.1rem 0.4rem', color: 'var(--text-secondary)' }}>Moved out</span>}
                                {/* TENANT-ERASURE-001: without this, an erased
                                    tenant just reads "Tenant A" with no
                                    indication why — indistinguishable from
                                    someone genuinely named that. */}
                                {t.erased_at && <span style={{ fontSize: '0.7rem', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '0.25rem', padding: '0.1rem 0.4rem', color: '#92400e', marginLeft: '0.3rem' }}>🔒 Erased</span>}
                                {(() => {
                                  const issues = getIrishComplianceIssues(t);
                                  return issues.length > 0 ? (
                                    <div style={{ marginTop: '0.25rem', display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                                      {issues.map(issue => (
                                        <span key={issue.code} title={issue.detail || issue.label}
                                          style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem', borderRadius: '0.25rem', fontWeight: 600,
                                            background: issue.severity === 'danger' ? '#fee2e2' : '#fef3c7',
                                            color: issue.severity === 'danger' ? '#dc2626' : '#92400e' }}>
                                          🇮🇪 {issue.label}
                                        </span>
                                      ))}
                                    </div>
                                  ) : null;
                                })()}
                              </td>
                              <td style={{ padding: '0.75rem 1rem' }}>
                                {groupBy === 'alpha' && <div style={{ fontSize: '0.8rem' }}>{t.property_address || '—'}</div>}
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Room: {getRoomName(t.property_id, t.room_id)}</div>
                                {isAgencyRow && <span style={{ fontSize: '0.7rem', color: 'var(--primary)', fontWeight: 500 }}>🏢 Agency</span>}
                              </td>
                              <td style={{ padding: '0.75rem 1rem' }}>
                                {fmtRent(t.rent_amount, currency)}
                                {t.payment_due_day && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Due day {t.payment_due_day}</div>}
                              </td>
                              <td style={{ padding: '0.75rem 1rem' }}>
                                <span style={{ color: ls.color, fontWeight: 500, fontSize: '0.8rem' }}>{ls.label}</span>
                              </td>
                              <td style={{ padding: '0.75rem 1rem' }}>
                                <div style={{ fontSize: '0.75rem', color: done === items.length ? 'var(--success)' : 'var(--warning)' }}>
                                  {done}/{items.length}{done === items.length ? ' ✅' : ' ⏳'}
                                </div>
                              </td>
                              <td style={{ padding: '0.75rem 1rem' }}>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                  {isAgencyRow && !t.comms_override ? <span>🏢 Agency</span> : <>{t.email_reminders ? '🔔' : '🔕'} {t.email_receipts ? '🧾' : ''}</>}
                                </div>
                              </td>
                              <td style={{ padding: '0.75rem 1rem' }} onClick={e => e.stopPropagation()}>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <button className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                                    onClick={() => navigate(`/tenants/${t.id}`)}>✏️ Edit</button>
                                  {t.status === 'active' && (
                                    <button className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', color: 'var(--danger)' }}
                                      onClick={() => { setMoveOutTarget(t); setMoveOutDateInput(todayISO()); setMoveOutError(''); setShowMoveOutModal(true); }}>Move Out</button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* ── BTL/Single Let/etc: tenancy cards ── */}
                {!isHMO && groupBy !== 'alpha' && (
                  <div style={{ padding: '0.75rem', display: 'grid', gap: '0.75rem' }}>
                    {group.tenancies.map(tenancy => {
                      const lead = tenancy.tenants.find(t => t.is_lead_tenant) || tenancy.tenants[0];
                      const joint = tenancy.tenants.filter(t => !t.is_lead_tenant && t.status === 'active');
                      if (!lead) return null;
                      const ls = leaseStatus(lead);
                      const currency = lead.currency || 'GBP';
                      const checklist = lead.checklist || {};
                      const items = CHECKLIST_ITEMS[effectiveJurisdiction(lead)] || CHECKLIST_ITEMS.england;
                      const done = items.filter(i => checklist[i.key]).length;
                      const isAgency = lead.management_type === 'agency';
                      const compIssues = getIrishComplianceIssues(lead);
                      return (
                        <div key={tenancy.ref} style={{ border: '1px solid var(--border)', borderRadius: '0.5rem', overflow: 'hidden' }}>
                          {/* Tenancy header — lead tenant */}
                          <div style={{ padding: '0.75rem 1rem', background: 'var(--bg-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', cursor: 'pointer' }}
                            onClick={() => navigate(`/tenants/${lead.id}`)}>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{lead.first_name} {lead.last_name}</span>
                                <span style={{ fontSize: '0.65rem', background: 'var(--primary)', color: '#fff', borderRadius: '0.25rem', padding: '0.1rem 0.35rem', fontWeight: 600 }}>Lead</span>
                                {lead.status === 'moved_out' && <span style={{ fontSize: '0.65rem', background: 'var(--bg-light)', border: '1px solid var(--border)', borderRadius: '0.25rem', padding: '0.1rem 0.35rem', color: 'var(--text-secondary)' }}>Moved out</span>}
                                {lead.erased_at && <span style={{ fontSize: '0.65rem', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '0.25rem', padding: '0.1rem 0.35rem', color: '#92400e' }}>🔒 Erased</span>}
                                {isAgency && <span style={{ fontSize: '0.65rem', color: 'var(--primary)', fontWeight: 600 }}>🏢 Agency</span>}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                                {lead.email || '—'}{lead.phone ? ` · ${lead.phone}` : ''}
                              </div>
                              {compIssues.length > 0 && (
                                <div style={{ marginTop: '0.35rem', display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                                  {compIssues.map(issue => (
                                    <span key={issue.code} title={issue.detail || issue.label}
                                      style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem', borderRadius: '0.25rem', fontWeight: 600,
                                        background: issue.severity === 'danger' ? '#fee2e2' : '#fef3c7',
                                        color: issue.severity === 'danger' ? '#dc2626' : '#92400e' }}>
                                      🇮🇪 {issue.label}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '1rem' }}>
                              <div style={{ fontWeight: 700, color: 'var(--success)', fontSize: '1rem' }}>{fmtRent(lead.rent_amount, currency)}/mo</div>
                              <div style={{ fontSize: '0.8rem', color: ls.color, fontWeight: 500, marginTop: '0.15rem' }}>{ls.label}</div>
                              <div style={{ fontSize: '0.75rem', color: done === items.length ? 'var(--success)' : 'var(--warning)', marginTop: '0.15rem' }}>Checklist {done}/{items.length}{done === items.length ? ' ✅' : ' ⏳'}</div>
                            </div>
                          </div>

                          {/* Tenancy details strip */}
                          <div style={{ padding: '0.5rem 1rem', display: 'flex', gap: '1.5rem', fontSize: '0.8rem', color: 'var(--text-secondary)', borderBottom: joint.length > 0 ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
                            <span>📋 {tenancyTypes.find(ty => ty.value === lead.tenancy_type)?.label || lead.tenancy_type || '—'}</span>
                            <span>📅 {lead.lease_start ? fmtDate(lead.lease_start) : '—'} → {lead.lease_end ? fmtDate(lead.lease_end) : 'Periodic'}</span>
                            {lead.payment_due_day && <span>💰 Due day {lead.payment_due_day}</span>}
                            {lead.deposit_amount && <span>🔒 Deposit {fmtRent(lead.deposit_amount, currency)}</span>}
                          </div>

                          {/* Joint tenants */}
                          {joint.length > 0 && (
                            <div style={{ padding: '0.5rem 1rem', background: '#fafafa' }}>
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>Joint Tenants</div>
                              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                                {joint.map(jt => (
                                  <div key={jt.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0.6rem', background: '#fff', border: '1px solid var(--border)', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8rem' }}
                                    onClick={() => navigate(`/tenants/${jt.id}`)}>
                                    <span style={{ fontWeight: 500 }}>{jt.first_name} {jt.last_name}</span>
                                    {jt.email && <span style={{ color: 'var(--text-secondary)' }}>{jt.email}</span>}
                                    <span style={{ color: 'var(--primary)', fontSize: '0.7rem' }}>→</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Add / Edit Modal ─────────────────────────────────────────────── */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal" style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editTenant ? `✏️ Edit — ${editTenant.first_name} ${editTenant.last_name}` : '+ Add Tenant'}</span>
              <button className="modal-close" onClick={() => setModalOpen(false)}>×</button>
            </div>

            {error && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{error}</div>}

            {isAgency && (
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.875rem', color: '#1e40af' }}>
                🏢 This property is agency managed. Rent reminders and receipts are off by default.
              </div>
            )}

            <form onSubmit={handleSave}>
              {/* Property & Room */}
              {!editTenant && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Property & Room</div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Property *</label>
                      <select value={form.property_id} onChange={e => handlePropertyChange(e.target.value)} required>
                        <option value="">Select property...</option>
                        {properties.filter(p => !p.archived).map(p => (
                          <option key={p.id} value={p.id}>{p.address}</option>
                        ))}
                      </select>
                    </div>
                    {isHMO ? (
                      <div className="form-group">
                        <label>Room *</label>
                        <select value={form.room_id} onChange={e => set('room_id', e.target.value)} required disabled={!form.property_id}>
                          <option value="">Select room...</option>
                          {availableRooms.map(r => (
                            <option key={r.id} value={r.id}>{r.roomName} {r.status === 'occupied' ? '(Occupied)' : ''}</option>
                          ))}
                        </select>
                      </div>
                    ) : form.property_id && (
                      <div className="form-group">
                        <label>Room</label>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', padding: '0.5rem 0' }}>
                          Not applicable — this tenant is assigned to the whole property.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Personal Details */}
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Personal Details</div>
                <div className="form-grid">
                  <div className="form-group"><label>First Name *</label><input value={form.first_name} onChange={e => set('first_name', e.target.value)} required /></div>
                  <div className="form-group"><label>Last Name</label><input value={form.last_name} onChange={e => set('last_name', e.target.value)} /></div>
                  <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => set('email', e.target.value)} /></div>
                  <div className="form-group"><label>Phone</label><input value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
                  <div className="form-group"><label>Date of Birth</label><input type="date" value={form.date_of_birth} onChange={e => set('date_of_birth', e.target.value)} /></div>
                </div>
              </div>

              {/* Tenancy Details */}
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tenancy Details</div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Tenancy Type</label>
                    <select value={form.tenancy_type} onChange={e => set('tenancy_type', e.target.value)}>
                      {tenancyTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>

                  {!editTenant && (
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label>Tenant Status</label>
                      <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button type="button" onClick={() => set('status', 'active')} className={`btn ${form.status !== 'moved_out' ? 'btn-success' : 'btn-secondary'}`} style={{ flex: 1 }}>Current Tenant</button>
                        <button type="button" onClick={() => set('status', 'moved_out')} className={`btn ${form.status === 'moved_out' ? 'btn-success' : 'btn-secondary'}`} style={{ flex: 1 }}>Past Tenant (moved out)</button>
                      </div>
                      {form.status === 'moved_out' && (
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                          For backdating — add someone who used to live here so historical rent payments can be correctly attributed to them. Requires a Lease End date.
                        </p>
                      )}
                    </div>
                  )}

                  <div className="form-group"><label>Lease Start</label><input type="date" value={form.lease_start} onChange={e => set('lease_start', e.target.value)} /></div>
                  <div className="form-group">
                    <label>Lease End {form.status === 'moved_out' ? <span style={{ color: 'var(--danger)', fontSize: '0.75rem' }}>*required for a past tenant</span> : <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>(leave blank for periodic)</span>}</label>
                    <input type="date" value={form.lease_end} onChange={e => set('lease_end', e.target.value)} required={form.status === 'moved_out'} />
                  </div>
                  {overlapWarning && (
                    <div style={{ gridColumn: '1 / -1', background: '#fef3c7', border: '1px solid #fde047', borderRadius: '0.5rem', padding: '0.75rem 1rem', fontSize: '0.8rem', color: '#92400e' }}>
                      ⚠️ {overlapWarning}
                    </div>
                  )}
                  <div className="form-group"><label>Monthly Rent ({currencySymbol(selectedProperty?.currency)})</label><input type="number" step="0.01" value={form.rent_amount} onChange={e => set('rent_amount', e.target.value)} /></div>
                  <div className="form-group">
                    <label>Payment Due Day</label>
                    <select value={form.payment_due_day} onChange={e => set('payment_due_day', parseInt(e.target.value))}>
                      {Array.from({ length: 28 }, (_, i) => <option key={i+1} value={i+1}>{i+1}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Deposit */}
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Deposit</div>
                <div className="form-grid">
                  <div className="form-group"><label>Deposit Amount ({currencySymbol(selectedProperty?.currency)})</label><input type="number" step="0.01" value={form.deposit_amount} onChange={e => set('deposit_amount', e.target.value)} /></div>
                  <div className="form-group">
                    <label>Protection Scheme</label>
                    <select value={form.deposit_scheme} onChange={e => set('deposit_scheme', e.target.value)}>
                      <option value="">Select scheme...</option>
                      {depositSchemes.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label>Scheme Reference</label><input value={form.deposit_reference} onChange={e => set('deposit_reference', e.target.value)} placeholder="Reference number" /></div>
                  <div className="form-group"><label>Deposit Paid Date</label><input type="date" value={form.deposit_paid_date} onChange={e => set('deposit_paid_date', e.target.value)} /></div>
                  <div className="form-group"><label>Protected Date</label><input type="date" value={form.deposit_protected_date} onChange={e => set('deposit_protected_date', e.target.value)} /></div>
                </div>
              </div>

              {/* Right to Rent (non-Ireland) / RTB (Ireland) — collapsed by
                  default for agency-managed properties (see state comment
                  above). Self-managed properties render exactly as before. */}
              {isAgency && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <button
                    type="button"
                    onClick={() => setShowComplianceDetails(v => !v)}
                    style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '0.375rem', padding: '0.5rem 0.75rem', fontSize: '0.8125rem', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                  >
                    {showComplianceDetails ? '▾' : '▸'} Right to Rent &amp; move-in checklist — usually handled by the agency, optional here
                  </button>
                </div>
              )}
              {(!isAgency || showComplianceDetails) && (isIreland ? (
                <div style={{ marginBottom: '1.25rem' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>🇮🇪 RTB & RPZ</div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={form.rtb_registered} onChange={e => set('rtb_registered', e.target.checked)} />
                        RTB Registration completed
                      </label>
                    </div>
                    <div className="form-group"><label>RTB Reference Number</label><input value={form.rtb_registration_number} onChange={e => set('rtb_registration_number', e.target.value)} /></div>
                    <div className="form-group"><label>RTB Registration Date</label><input type="date" value={form.rtb_registration_date} onChange={e => set('rtb_registration_date', e.target.value)} /></div>
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={form.rpz_property} onChange={e => set('rpz_property', e.target.checked)} />
                        Property in Rent Pressure Zone (RPZ)
                      </label>
                    </div>
                    {form.rpz_property && <div className="form-group"><label>Last Rent Review Date</label><input type="date" value={form.last_rent_review_date} onChange={e => set('last_rent_review_date', e.target.value)} /></div>}
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: '1.25rem' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Right to Rent</div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={form.right_to_rent_checked} onChange={e => set('right_to_rent_checked', e.target.checked)} />
                        Right to Rent check completed
                      </label>
                    </div>
                    <div className="form-group"><label>Document Type</label><input value={form.right_to_rent_doc} onChange={e => set('right_to_rent_doc', e.target.value)} placeholder="e.g. UK Passport, BRP" /></div>
                    <div className="form-group"><label>Check Date</label><input type="date" value={form.right_to_rent_date} onChange={e => set('right_to_rent_date', e.target.value)} /></div>
                    <div className="form-group"><label>Document Expiry</label><input type="date" value={form.right_to_rent_expiry} onChange={e => set('right_to_rent_expiry', e.target.value)} /></div>
                  </div>
                </div>
              ))}

              {/* Emergency Contact */}
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Emergency Contact</div>
                <div className="form-grid">
                  <div className="form-group"><label>Name</label><input value={form.emergency_name} onChange={e => set('emergency_name', e.target.value)} /></div>
                  <div className="form-group"><label>Phone</label><input value={form.emergency_phone} onChange={e => set('emergency_phone', e.target.value)} /></div>
                  <div className="form-group"><label>Relationship</label><input value={form.emergency_relationship} onChange={e => set('emergency_relationship', e.target.value)} placeholder="e.g. Parent, Partner" /></div>
                </div>
              </div>

              {/* Guarantor */}
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Guarantor <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional)</span></div>
                <div className="form-grid">
                  <div className="form-group"><label>Name</label><input value={form.guarantor_name} onChange={e => set('guarantor_name', e.target.value)} /></div>
                  <div className="form-group"><label>Email</label><input type="email" value={form.guarantor_email} onChange={e => set('guarantor_email', e.target.value)} /></div>
                  <div className="form-group"><label>Phone</label><input value={form.guarantor_phone} onChange={e => set('guarantor_phone', e.target.value)} /></div>
                </div>
              </div>

              {/* Move-in Checklist — collapsed with the Right to Rent
                  section above for agency-managed properties. */}
              {(!isAgency || showComplianceDetails) && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <ChecklistPanel
                    checklist={form.checklist}
                    jurisdiction={jurisdiction}
                    onChange={v => set('checklist', v)}
                    readOnly={false}
                  />
                </div>
              )}

              {/* Communications */}
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Communications</div>
                {isAgency && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                      <input type="checkbox" checked={form.comms_override} onChange={e => set('comms_override', e.target.checked)} />
                      Override agency management — send comms directly to tenant
                    </label>
                  </div>
                )}
                {(!isAgency || form.comms_override) && (
                  <div style={{ display: 'flex', gap: '1.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                      <input type="checkbox" checked={form.email_reminders} onChange={e => set('email_reminders', e.target.checked)} />
                      Send rent reminders
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                      <input type="checkbox" checked={form.email_receipts} onChange={e => set('email_receipts', e.target.checked)} />
                      Send rent receipts
                    </label>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                <label>Notes</label>
                <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} placeholder="Any additional notes about this tenant..." style={{ width: '100%', resize: 'vertical' }} />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : editTenant ? '✅ Save Changes' : '✅ Add Tenant'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showMoveOutModal && moveOutTarget && (
        <div className="modal-overlay" onClick={() => !movingOut && setShowMoveOutModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <span className="modal-title">🚪 Move {moveOutTarget.first_name} {moveOutTarget.last_name} Out</span>
              <button type="button" className="modal-close" onClick={() => setShowMoveOutModal(false)} disabled={movingOut}>×</button>
            </div>
            <div className="form-group">
              <label>When did they actually move out?</label>
              <input
                type="date"
                value={moveOutDateInput}
                max={todayISO()}
                onChange={e => setMoveOutDateInput(e.target.value)}
                autoFocus
              />
              <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                Defaults to today — change this if they actually left earlier (e.g. you're only recording it now, after the fact). This date is what arrears and rent-owed calculations use going forward.
              </small>
            </div>
            {moveOutError && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{moveOutError}</div>}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowMoveOutModal(false)} disabled={movingOut}>Cancel</button>
              <button type="button" className="btn btn-danger" disabled={movingOut} onClick={handleMoveOut}>
                {movingOut ? 'Saving…' : 'Confirm Move Out'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
